import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import axios from 'axios';
import './VerificationForm.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://proofofhuman.tevas.in:3001/api/v1';

const VerificationForm = () => {
  const [step, setStep] = useState(1);
  const [walletAddress, setWalletAddress] = useState('');
  const [walletConnected, setWalletConnected] = useState(false);
  const [verificationData, setVerificationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    documentType: 'passport',
    documentFile: null,
    selfieFile: null,
    socialVouches: []
  });

  useEffect(() => {
    checkWalletConnection();
  }, []);

  const checkWalletConnection = async () => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const accounts = await provider.listAccounts();
        if (accounts.length > 0) {
          setWalletAddress(accounts[0]);
          setWalletConnected(true);
        }
      } catch (err) {
        console.error('Error checking wallet:', err);
      }
    }
  };

  const connectWallet = async () => {
    if (typeof window.ethereum === 'undefined') {
      setError('Please install MetaMask to continue');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      await provider.send("eth_requestAccounts", []);
      const signer = provider.getSigner();
      const address = await signer.getAddress();

      setWalletAddress(address);
      setWalletConnected(true);
      setStep(2);
      setSuccess('Wallet connected successfully!');

    } catch (err) {
      setError(err.message || 'Failed to connect wallet');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      // Convert to base64 for demo
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          [type]: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const submitVerification = async () => {
    try {
      setLoading(true);
      setError('');

      // Submit verification request
      const response = await axios.post(`${API_BASE_URL}/verification/submit`, {
        walletAddress,
        documentType: formData.documentType,
        documentData: formData.documentFile,
        biometricData: formData.selfieFile,
        socialVouches: formData.socialVouches
      });

      setVerificationData(response.data.data);
      setStep(3);
      setSuccess('Verification submitted successfully!');

    } catch (err) {
      setError(err.response?.data?.error || 'Verification submission failed');
    } finally {
      setLoading(false);
    }
  };

  const registerOnBlockchain = async () => {
    try {
      setLoading(true);
      setError('');

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();

      // In production, call smart contract here
      // For demo, simulate blockchain registration
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Register in database
      await axios.post(`${API_BASE_URL}/verification/register`, {
        walletAddress,
        identityHash: verificationData.identityHash,
        nullifierHash: verificationData.nullifierHash,
        verificationLevel: verificationData.verificationLevel,
        expiresAt: verificationData.expiresAt,
        ipfsHash: verificationData.ipfsHash,
        nftTokenId: Math.floor(Math.random() * 10000),
        txHash: '0x' + '1'.repeat(64) // Simulated tx hash
      });

      setStep(4);
      setSuccess('Registration complete! Your ProofOfHuman NFT has been minted.');

    } catch (err) {
      setError(err.response?.data?.error || 'Blockchain registration failed');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="step-content">
            <div className="step-header">
              <div className="step-number">01</div>
              <h2>Connect Your Wallet</h2>
              <p>Link your wallet to begin the verification process</p>
            </div>

            {!walletConnected ? (
              <button 
                className="primary-btn connect-btn"
                onClick={connectWallet}
                disabled={loading}
              >
                {loading ? (
                  <span className="loading-spinner"></span>
                ) : (
                  <>
                    <svg className="wallet-icon" viewBox="0 0 24 24" fill="none">
                      <path d="M21 18v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2"/>
                      <path d="M8 12h13m-7-4l4 4-4 4" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    Connect MetaMask
                  </>
                )}
              </button>
            ) : (
              <div className="wallet-connected">
                <div className="success-icon">✓</div>
                <p>Wallet Connected</p>
                <code className="wallet-address">{walletAddress}</code>
                <button className="primary-btn" onClick={() => setStep(2)}>
                  Continue to Verification
                </button>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <div className="step-header">
              <div className="step-number">02</div>
              <h2>Upload Verification Documents</h2>
              <p>Provide your identification and biometric data</p>
            </div>

            <div className="form-section">
              <label htmlFor="documentType">Document Type</label>
              <select
                id="documentType"
                value={formData.documentType}
                onChange={(e) => setFormData(prev => ({ ...prev, documentType: e.target.value }))}
                className="form-select"
              >
                <option value="passport">Passport</option>
                <option value="drivers_license">Driver's License</option>
                <option value="national_id">National ID</option>
              </select>
            </div>

            <div className="form-section">
              <label htmlFor="documentFile">ID Document</label>
              <div className="file-upload">
                <input
                  type="file"
                  id="documentFile"
                  accept="image/*,application/pdf"
                  onChange={(e) => handleFileChange(e, 'documentFile')}
                  className="file-input"
                />
                <label htmlFor="documentFile" className="file-label">
                  {formData.documentFile ? '✓ Document Uploaded' : '📄 Upload Document'}
                </label>
              </div>
            </div>

            <div className="form-section">
              <label htmlFor="selfieFile">Selfie (Biometric Verification)</label>
              <div className="file-upload">
                <input
                  type="file"
                  id="selfieFile"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'selfieFile')}
                  className="file-input"
                />
                <label htmlFor="selfieFile" className="file-label">
                  {formData.selfieFile ? '✓ Selfie Uploaded' : '📸 Upload Selfie'}
                </label>
              </div>
            </div>

            <button
              className="primary-btn"
              onClick={submitVerification}
              disabled={loading || !formData.documentFile}
            >
              {loading ? <span className="loading-spinner"></span> : 'Submit Verification'}
            </button>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <div className="step-header">
              <div className="step-number">03</div>
              <h2>Blockchain Registration</h2>
              <p>Mint your ProofOfHuman NFT on the blockchain</p>
            </div>

            <div className="verification-summary">
              <div className="summary-item">
                <span className="label">Verification Level:</span>
                <span className={`badge badge-${verificationData?.verificationLevel?.toLowerCase()}`}>
                  {verificationData?.verificationLevel}
                </span>
              </div>
              <div className="summary-item">
                <span className="label">Valid Until:</span>
                <span>{new Date(verificationData?.expiresAt).toLocaleDateString()}</span>
              </div>
              <div className="summary-item">
                <span className="label">IPFS Hash:</span>
                <code className="ipfs-hash">{verificationData?.ipfsHash}</code>
              </div>
            </div>

            <div className="info-box">
              <p>Your verification data will be encrypted and stored on IPFS. Only a cryptographic hash will be stored on-chain, preserving your privacy.</p>
            </div>

            <button
              className="primary-btn"
              onClick={registerOnBlockchain}
              disabled={loading}
            >
              {loading ? <span className="loading-spinner"></span> : 'Register on Blockchain'}
            </button>
          </div>
        );

      case 4:
        return (
          <div className="step-content">
            <div className="step-header">
              <div className="success-icon-large">🎉</div>
              <h2>Verification Complete!</h2>
              <p>Your ProofOfHuman identity has been created</p>
            </div>

            <div className="completion-info">
              <div className="nft-card">
                <div className="nft-image">PoH</div>
                <h3>ProofOfHuman NFT</h3>
                <p>Level: {verificationData?.verificationLevel}</p>
              </div>

              <div className="next-steps">
                <h3>What's Next?</h3>
                <ul>
                  <li>✓ Use your PoH identity across supported platforms</li>
                  <li>✓ Enjoy privacy-preserving authentication</li>
                  <li>✓ Participate in verified-only communities</li>
                </ul>
              </div>
            </div>

            <button className="primary-btn" onClick={() => window.location.reload()}>
              Verify Another Address
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="verification-container">
      <div className="background-pattern"></div>
      
      <header className="app-header">
        <div className="logo">
          <div className="logo-icon">PoH</div>
          <span>ProofOfHuman</span>
        </div>
        {walletConnected && (
          <div className="wallet-status">
            <div className="status-indicator"></div>
            <span>{walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</span>
          </div>
        )}
      </header>

      <main className="main-content">
        <div className="progress-bar">
          {[1, 2, 3, 4].map(num => (
            <div
              key={num}
              className={`progress-step ${step >= num ? 'active' : ''} ${step > num ? 'completed' : ''}`}
            >
              <div className="progress-dot">{step > num ? '✓' : num}</div>
              <span className="progress-label">
                {num === 1 && 'Connect'}
                {num === 2 && 'Verify'}
                {num === 3 && 'Register'}
                {num === 4 && 'Complete'}
              </span>
            </div>
          ))}
        </div>

        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            <span className="alert-icon">✓</span>
            {success}
          </div>
        )}

        <div className="form-card">
          {renderStep()}
        </div>
      </main>

      <footer className="app-footer">
        <p>Secured by blockchain technology • Privacy-preserving • Decentralized</p>
      </footer>
    </div>
  );
};

export default VerificationForm;
